import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HearthPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Hearth" (`/hearth`) — a self-contained ONBOARDING paywall for Private Photo
// Vault. (This run's type was drawn at random → onboarding.)
//
// Concept — HOME / BELONGING. Every onboarding paywall in this repo pitches
// privacy as wonder (Aurora cosmos, Abyss deep), preservation (Amber), exposure
// (Sightline seam), it's-handled (Firstlight boot), completion (Full Circle
// ring), or a gift (Handsel). None frames the vault as a *home* — a warm, private
// place that is yours, where everything you love is kept close and safe. That's
// this one: the new user isn't "securing files," they're moving into a warm room
// and lighting the hearth. Premium is the moment a cold new place becomes home.
//
// Divergence (vs the saturated onboarding + skeuomorphic-scene families):
//   - Layout:      a warm LIGHT living-room MANTELPIECE tableau — a shelf of your
//                  first framed memories over a glowing hearth; no centered card,
//                  no orbit, no door, no ring, no gift box.
//   - Interaction: KINDLE THE HEARTH — the fire catches and warmth sweeps left→
//                  right across the mantel; each framed memory warms and clasps a
//                  small brass lock as it's "brought home," and a HOME · only you
//                  nameplate settles. Auto-warms on load, then hands over a relight.
//   - Register:    cozy welcome-home warmth — "make it home," not awe or urgency.
//   - Art:         warm ivory room + a golden hearth glow; brand blue is the KEPT/
//                  protected accent (locks, nameplate, featured yearly, CTA). Light,
//                  not a dark vault, glow-card, or particle field.
//   - Value:       belonging — a private place of your own, kept warm all year.
//   - Motion:      a flickering hearth, a warm light bloom crossing the frames,
//                  brass locks clasping in sequence — warmth arriving, never a
//                  grey→colour reveal (the memories are full colour throughout).
//
// Honest / App Store compliant: the yearly price is the largest, most prominent
// price for the yearly product; monthly is present but smaller and secondary.
// Savings + trial are computed from the real debug prices. No fake countdown,
// scarcity, or data-loss language — Premium is "keeping the hearth warm," never
// "pay or lose your photos." Renders on built-in debug data; sample memories reuse
// bundled public/byheart/*.jpg (no network egress).
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (file: string) => `${PUB}/byheart/${file}.jpg`

type PlanKey = "yearly" | "monthly"

// The first memories set on the mantel. Each carries a slight easel tilt so the
// row reads like a real shelf of frames, not a grid.
const MANTEL = [
  { img: "m03", rot: -3 },
  { img: "m07", rot: 2 },
  { img: "m11", rot: -1.5 },
  { img: "m14", rot: 2.5 },
  { img: "m17", rot: -2 },
]

// "What makes it home" — real Premium capabilities, framed as the warmth of a
// place that keeps everything you love. Icons are inline so the page is fully
// self-contained.
const COMFORTS: {
  key: string
  title: string
  note: string
  icon: JSX.Element
}[] = [
  {
    key: "backup",
    title: "Kept safe, automatically",
    note: "every new photo & video, backed up for you",
    icon: (
      <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden>
        <path
          d="M7 18h9.5a3.5 3.5 0 0 0 .4-6.98A5 5 0 0 0 7.6 9.2 4 4 0 0 0 7 18Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M12 15.5v-4.2m0 0-1.7 1.7M12 11.3l1.7 1.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    key: "lock",
    title: "Only you can open it",
    note: "locked with your PIN — private & encrypted",
    icon: (
      <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden>
        <path
          d="M8 10V8a4 4 0 0 1 8 0v2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <rect
          x="6"
          y="10"
          width="12"
          height="9"
          rx="2.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle cx="12" cy="14.4" r="1.35" fill="currentColor" />
      </svg>
    ),
  },
  {
    key: "sorted",
    title: "Everything in its place",
    note: "albums sorted for you, so the mantel stays tidy",
    icon: (
      <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden>
        <rect
          x="4"
          y="5"
          width="7"
          height="7"
          rx="1.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <rect
          x="13"
          y="5"
          width="7"
          height="7"
          rx="1.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <rect
          x="4"
          y="14"
          width="7"
          height="5"
          rx="1.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M13 16.5h7m-3.5-3v7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    key: "everywhere",
    title: "In full quality, everywhere",
    note: "your originals, on every device",
    icon: (
      <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden>
        <rect
          x="3.5"
          y="5"
          width="12"
          height="9"
          rx="1.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <rect
          x="14.5"
          y="9.5"
          width="6"
          height="9.5"
          rx="1.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M7 17.5h4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
]

function throughMonthYear() {
  const d = new Date()
  d.setFullYear(d.getFullYear() + 1)
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" })
}

function HearthPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  useEffect(() => {
    // Blend the safe-area insets into the warm room so it reads as one space.
    actions.setHeaderInsetColor("#f7efe2")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])
  const trialDays = yearlyProduct?.introPricing?.period ?? 7
  const keptThrough = useMemo(throughMonthYear, [])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── kindle the hearth: the fire catches, warmth sweeps the mantel ──────────
  const [lit, setLit] = useState(reduce)
  const [warmed, setWarmed] = useState(reduce ? MANTEL.length : 0)
  const timers = useRef<number[]>([])

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }

  const kindle = useCallback(() => {
    clearTimers()
    if (reduce) {
      setLit(true)
      setWarmed(MANTEL.length)
      return
    }
    setWarmed(0)
    setLit(true)
    // warmth reaches each frame in turn, left to right
    MANTEL.forEach((_, i) => {
      const t = window.setTimeout(
        () => setWarmed((w) => Math.max(w, i + 1)),
        520 + i * 260,
      )
      timers.current.push(t)
    })
  }, [reduce])

  // Auto-light the hearth on arrival so the very first thing a new user sees is
  // a cold room becoming warm — then it stays lit and can be relit.
  useEffect(() => {
    if (reduce) {
      setLit(true)
      setWarmed(MANTEL.length)
      return
    }
    const t = window.setTimeout(kindle, 420)
    timers.current.push(t)
    return clearTimers
  }, [reduce, kindle])

  const allHome = warmed >= MANTEL.length

  const handleClose = () => native.postNativeEvent("close")
  const buy = useCallback(() => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div className={`${styles.page} ${reduce ? styles.noMotion : ""}`}>
      {/* chrome ------------------------------------------------------------- */}
      <header className={styles.top}>
        <span className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 3.5 4.5 9.2V20h5v-5.2h5V20h5V9.2Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Private Photo Vault
          <span className={styles.brandTag}>Premium</span>
        </span>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="14" height="14">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* headline ----------------------------------------------------------- */}
      <section className={styles.hero}>
        <span className={styles.kicker}>Welcome home</span>
        <h1 className={styles.head}>
          A warm, private place
          <br />
          for everything you love.
        </h1>
        <p className={styles.sub}>
          This is your vault — a room only you can open. Light the hearth and
          make it home.
        </p>
      </section>

      {/* the hearth tableau ------------------------------------------------- */}
      <section className={styles.room}>
        <div
          className={`${styles.mantelScene} ${lit ? styles.sceneLit : ""}`}
          aria-label="Your first memories on the mantel"
        >
          {/* warm light cast from the hearth over the whole shelf */}
          <span className={styles.glow} aria-hidden />

          {/* the shelf of framed memories */}
          <div className={styles.mantelShelfRow}>
            {MANTEL.map((m, i) => {
              const home = warmed > i
              return (
                <figure
                  key={m.img}
                  className={`${styles.frame} ${home ? styles.frameHome : ""}`}
                  style={{
                    ["--rot" as any]: `${m.rot}deg`,
                    ["--d" as any]: `${i * 60}ms`,
                  }}
                >
                  <img src={photo(m.img)} alt="" draggable={false} />
                  <span className={styles.frameWarm} aria-hidden />
                  <span className={styles.lockChip} aria-hidden>
                    <svg viewBox="0 0 24 24" width="10" height="10">
                      <path
                        d="M8.5 10V8.4a3.5 3.5 0 0 1 7 0V10"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <rect
                        x="6.7"
                        y="10"
                        width="10.6"
                        height="8"
                        rx="2"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                </figure>
              )
            })}
          </div>

          {/* the mantel beam with the settling nameplate */}
          <div className={styles.mantel}>
            <span
              className={`${styles.nameplate} ${allHome ? styles.plateOn : ""}`}
            >
              <span className={styles.plateDot} aria-hidden />
              HOME · only you
            </span>
          </div>

          {/* the hearth: firebox + fire */}
          <div className={`${styles.hearth} ${lit ? styles.hearthLit : ""}`}>
            <div className={styles.firebox}>
              <span className={`${styles.fire} ${styles.fireA}`} aria-hidden />
              <span className={`${styles.fire} ${styles.fireB}`} aria-hidden />
              <span className={`${styles.fire} ${styles.fireC}`} aria-hidden />
              <span className={styles.embers} aria-hidden />
              <span className={styles.logs} aria-hidden />
            </div>
          </div>
        </div>

        {/* status line + relight ------------------------------------------- */}
        <div className={styles.statusRow}>
          <span className={styles.status}>
            <span
              className={`${styles.statusDot} ${lit ? styles.statusDotOn : ""}`}
              aria-hidden
            />
            {allHome
              ? "Home & warm — every memory kept"
              : "Lighting the hearth…"}
          </span>
          <button
            className={styles.relight}
            onClick={kindle}
            aria-label="Light the hearth again"
          >
            Light again
          </button>
        </div>
      </section>

      {/* what makes it home ------------------------------------------------- */}
      <ul className={styles.comforts}>
        {COMFORTS.map((c) => (
          <li key={c.key} className={styles.comfort}>
            <span className={styles.comfortIcon} aria-hidden>
              {c.icon}
            </span>
            <span className={styles.comfortText}>
              <b>{c.title}</b>
              <span>{c.note}</span>
            </span>
          </li>
        ))}
      </ul>

      {/* the offer ---------------------------------------------------------- */}
      <section className={styles.offer}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.on : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.radio} aria-hidden />
          <div className={styles.planYearBody}>
            <div className={styles.planYearTop}>
              <span className={styles.planName}>Keep the hearth warm</span>
              {savings > 0 && (
                <span className={styles.saveTag}>SAVE {savings}%</span>
              )}
            </div>
            <div className={styles.planYearNote}>
              a whole year, kept warm through <b>{keptThrough}</b>
            </div>
          </div>
          <div className={styles.planYearPrice}>
            <b>{yearlyPrice}</b>
            <span className={styles.per}>/yr</span>
            <span className={styles.perMo}>
              {yearlyPerMonth}/mo, billed yearly
            </span>
          </div>
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.on : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.radioSm} aria-hidden />
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthNote}>re-light each month</span>
          <span className={styles.planMonthPrice}>
            {monthlyPrice}
            <span className={styles.perSm}>/mo</span>
          </span>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span className={styles.ctaTop}>
            {plan === "yearly"
              ? trialDays
                ? `Start my ${trialDays}-day free trial`
                : "Make it home"
              : "Turn on Premium"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <p className={styles.reassure}>
          Your photos are always yours — the hearth only keeps them warm.
          Nothing is ever deleted.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden>·</span>
          <span className={styles.legalNote}>Sample memories shown</span>
        </div>
      </section>
    </div>
  )
}

export default HearthPaywall
